import React, { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { GetWorkflowSpend } from '../../Client/ManagementServiceClient';
import { CATEGORY_COLORS, categoryFor, itemLabel } from './labels';
import './styles.css';

/**
 * What this project has cost so far, shown inside the project.
 *
 * The billing page answers the date-ranged question once a month. This answers
 * it at the moment it changes a decision — while someone is looking at four
 * unrendered shots deciding whether to render them.
 *
 * Deliberately quiet when there is nothing to say: a project with no spend
 * renders nothing rather than a zero, because "$0.00" is a line of furniture
 * on a project that has not started costing anything.
 */
function WorkflowSpend({ workflowId }) {
    const [spend, setSpend] = useState(null);
    const [open, setOpen] = useState(false);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        if (!workflowId) return;
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await GetWorkflowSpend(idToken, workflowId);
            if (status !== 200) {
                setFailed(true);
                return;
            }
            setSpend(myJson);
        } catch (err) {
            // A cost figure is context, not the page. If it cannot be fetched
            // the project view carries on without it rather than erroring.
            setFailed(true);
        }
    }, [workflowId]);

    useEffect(() => { load(); }, [load]);

    if (failed || !spend) return null;

    const total = Number(spend.cost_usd || 0);
    if (total <= 0) return null;

    const formatPrice = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(value);

    const count = Number(spend.event_count || 0);
    const rows = spend.categories || [];

    return (
        <div className="workflow-spend">
            <button
                type="button"
                className="workflow-spend-head"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                <span className="workflow-spend-label">
                    <span className="workflow-spend-title">This project so far</span>
                    <span className="workflow-spend-sub">
                        {count} charge{count === 1 ? '' : 's'}
                    </span>
                </span>
                <span className="workflow-spend-total">{formatPrice(total)}</span>
                <span className="workflow-spend-sub">{open ? 'Hide' : 'Breakdown'}</span>
            </button>

            {open && rows.length > 0 && (
                <ul className="workflow-spend-rows">
                    {rows.map((row, index) => (
                        <li key={`${row.entity_flow}-${row.kind}-${index}`}>
                            <span
                                className="workflow-spend-swatch"
                                style={{ background: CATEGORY_COLORS[categoryFor(row.entity_flow)] || 'var(--ui-border-strong)' }}
                            />
                            <span>{itemLabel(row.entity_flow, row.kind, row.event_count)}</span>
                            <span className="workflow-spend-cost">{formatPrice(row.cost_usd || 0)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export { WorkflowSpend };
